import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Camera, X } from 'lucide-react'
import type { IScannerControls } from '@zxing/browser'

// BarcodeDetector (Shape Detection API) は lib.dom に型が無いため最小限を宣言する。
// Android Chrome 等ネイティブ対応ブラウザではこちらを優先し、
// 非対応ブラウザは @zxing/browser でデコードする
interface DetectedBarcode {
  rawValue: string
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => {
  detect(source: CanvasImageSource | ImageBitmap): Promise<DetectedBarcode[]>
}
declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorCtor
  }
}

// 機材ラベルで使われる形式のみに絞る（誤検出と検出負荷を減らす）
const BARCODE_FORMATS = [
  'qr_code',
  'code_128',
  'code_39',
  'ean_13',
  'itf',
  'data_matrix',
]

// 写真解析時の長辺の上限。大きすぎると zxing が遅く、小さすぎると細いバーコードが潰れる
const PHOTO_MAX_EDGE = 1920

// 写真を解析用に縮小して canvas へ描く
function drawScaled(bitmap: ImageBitmap): HTMLCanvasElement {
  const scale = Math.min(
    1,
    PHOTO_MAX_EDGE / Math.max(bitmap.width, bitmap.height)
  )
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(bitmap.width * scale))
  canvas.height = Math.max(1, Math.round(bitmap.height * scale))
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  return canvas
}

// 1Dコードは横向きでしか読めないため、縦向き写真用に90度回転版も作る
function rotate90(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = source.height
  canvas.height = source.width
  const ctx = canvas.getContext('2d')!
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate(Math.PI / 2)
  ctx.drawImage(source, -source.width / 2, -source.height / 2)
  return canvas
}

// 写真の一部分だけを切り出したタイル群（半分サイズ・50%重なり）。
// 写真全体では小さすぎて読めないコードや、zxing が「1枚につき1コード」しか
// 返さない制約を、領域を変えて複数回デコードすることで補う
function tileCanvases(source: HTMLCanvasElement): HTMLCanvasElement[] {
  const tileW = Math.floor(source.width / 2)
  const tileH = Math.floor(source.height / 2)
  if (tileW < 100 || tileH < 100) return []
  const result: HTMLCanvasElement[] = []
  for (const fx of [0, 0.25, 0.5]) {
    for (const fy of [0, 0.25, 0.5]) {
      const canvas = document.createElement('canvas')
      canvas.width = tileW
      canvas.height = tileH
      canvas
        .getContext('2d')!
        .drawImage(
          source,
          Math.floor(source.width * fx),
          Math.floor(source.height * fy),
          tileW,
          tileH,
          0,
          0,
          tileW,
          tileH
        )
      result.push(canvas)
    }
  }
  return result
}

interface BarcodeScannerDialogProps {
  open: boolean
  // 読み取り済み項目の表示（親でパースして入力欄へ反映した結果）
  captured: { label: string; value: string }[]
  onCode: (raw: string) => void
  onClose: () => void
}

// ラベルの QR・バーコードをカメラでライブ読み取りするモーダル。
// 検出した生文字列を onCode へ渡すだけで、項目への振り分けは親が行う
export default function BarcodeScannerDialog({
  open,
  captured,
  onCode,
  onClose,
}: BarcodeScannerDialogProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const zxingControlsRef = useRef<IScannerControls | null>(null)
  const seenRef = useRef<Set<string>>(new Set())
  // 開いている間のクリーンアップと競合しないよう onCode は ref 経由で呼ぶ
  const onCodeRef = useRef(onCode)
  onCodeRef.current = onCode

  const [cameraError, setCameraError] = useState(false)
  const [photoError, setPhotoError] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    seenRef.current = new Set()
    setCameraError(false)
    setPhotoError(false)

    const handleCode = (raw: string) => {
      if (!raw || seenRef.current.has(raw)) return
      seenRef.current.add(raw)
      onCodeRef.current(raw)
    }

    const start = async () => {
      const video = videoRef.current
      if (!video) return
      try {
        if (window.BarcodeDetector) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
          })
          if (cancelled) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }
          streamRef.current = stream
          video.srcObject = stream
          await video.play()
          const detector = new window.BarcodeDetector({
            formats: BARCODE_FORMATS,
          })
          intervalRef.current = setInterval(async () => {
            if (!videoRef.current || videoRef.current.readyState < 2) return
            try {
              const codes = await detector.detect(videoRef.current)
              for (const code of codes) handleCode(code.rawValue)
            } catch {
              // フレーム単位の検出失敗は無視して次のフレームを待つ
            }
          }, 300)
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser')
          const reader = new BrowserMultiFormatReader()
          const controls = await reader.decodeFromConstraints(
            { video: { facingMode: 'environment' } },
            video,
            (result) => {
              if (result) handleCode(result.getText())
            }
          )
          if (cancelled) controls.stop()
          else zxingControlsRef.current = controls
        }
      } catch {
        if (!cancelled) setCameraError(true)
      }
    }
    start()

    return () => {
      cancelled = true
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      zxingControlsRef.current?.stop()
      zxingControlsRef.current = null
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [open])

  // カメラを使えない環境向けに、撮影済み写真からの読み取りも用意する。
  // ラベルには QR + 複数の 1D バーコードが並ぶため、写真全体だけでなく
  // タイル分割した領域も順にデコードして複数コードをまとめて拾う
  const handlePhoto = async (file: File | undefined) => {
    if (!file) return
    setPhotoError(false)
    setPhotoBusy(true)
    try {
      const bitmap = await createImageBitmap(file)
      const base = drawScaled(bitmap)
      bitmap.close()

      const candidates = [base, ...tileCanvases(base)]
      let decodeCanvas: (canvas: HTMLCanvasElement) => Promise<string[]>
      if (window.BarcodeDetector) {
        const detector = new window.BarcodeDetector({
          formats: BARCODE_FORMATS,
        })
        decodeCanvas = async (canvas) =>
          (await detector.detect(canvas)).map((code) => code.rawValue)
      } else {
        // zxing は1回のデコードで1コードしか返さず、1Dコードは回転にも
        // 弱いため、90度回転版も候補に加えて総当たりする
        // （BarcodeDetector は回転不変・複数検出できるので不要）
        const rotated = rotate90(base)
        candidates.push(rotated, ...tileCanvases(rotated))
        const { BrowserMultiFormatReader } = await import('@zxing/browser')
        const { DecodeHintType } = await import('@zxing/library')
        const hints = new Map()
        hints.set(DecodeHintType.TRY_HARDER, true)
        const reader = new BrowserMultiFormatReader(hints)
        decodeCanvas = async (canvas) => {
          try {
            return [reader.decodeFromCanvas(canvas).getText()]
          } catch {
            return []
          }
        }
      }

      const found = new Set<string>()
      for (const canvas of candidates) {
        try {
          for (const raw of await decodeCanvas(canvas)) found.add(raw)
        } catch {
          // 個々の領域の失敗は無視して残りを試す
        }
        // 同期デコードが続いてUIが固まらないよう1領域ごとに描画へ譲る
        await new Promise((resolve) => setTimeout(resolve))
      }

      if (found.size === 0) {
        setPhotoError(true)
        return
      }
      for (const raw of found) {
        if (seenRef.current.has(raw)) continue
        seenRef.current.add(raw)
        onCodeRef.current(raw)
      }
    } catch {
      setPhotoError(true)
    } finally {
      setPhotoBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('scanner.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          {cameraError ? (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
              {t('scanner.noCamera')}
            </div>
          ) : (
            <div className="relative bg-black rounded-md overflow-hidden aspect-[4/3]">
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                muted
                playsInline
              />
            </div>
          )}
          <p className="text-xs text-gray-500">{t('scanner.hint')}</p>

          <label
            className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
              photoBusy
                ? 'text-gray-400 bg-gray-50 border-gray-200 cursor-wait'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 cursor-pointer'
            }`}
          >
            <Camera className="h-4 w-4" />
            {photoBusy ? t('scanner.processing') : t('scanner.takePhoto')}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              disabled={photoBusy}
              onChange={(e) => {
                handlePhoto(e.target.files?.[0])
                e.target.value = ''
              }}
            />
          </label>
          {photoError && (
            <p className="text-xs text-red-600">{t('scanner.photoNoCode')}</p>
          )}

          {captured.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1">
                {t('scanner.captured')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {captured.map(({ label, value }) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800"
                  >
                    <span className="font-medium">{label}:</span>
                    <span className="font-mono">{value}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            {t('scanner.done')}
          </button>
        </div>
      </div>
    </div>
  )
}
